import { Building2, FileWarning, GraduationCap } from 'lucide-react';
import { useEffect, useRef, type ReactElement, type ReactNode } from 'react';

import { BackLink } from '@/components/layout/BackLink';
import { TO_PANEL, useBackTarget } from '@/lib/back-nav';

import { GatedChrome, RestrictedPanel } from '@/components/auth/GatedChrome';
import { Pagination } from '@/components/ui/Pagination';
import { ExportMenu } from '@/components/ui/ExportMenu';
import { StatePanel } from '@/components/ui/StatePanel';
import { PermissionErrorPanel } from '@/components/auth/PermissionErrorPanel';
import { usePermissionState } from '@/hooks/usePermission';
import { useActiveCompany } from '@/lib/active-company-context';

import { DocumentDetail } from './components/DocumentDetail';
import { TrainingKpis } from './components/TrainingKpis';
import { TrainingSkeleton } from './components/TrainingSkeleton';
import { TrainingTable } from './components/TrainingTable';
import { errorCopy } from './presentation';
import { PAGE_SIZE, useTrainingOverview } from './use-training';
import { useTrainingUrlState } from './use-url-state';

const BREADCRUMB = [
  { label: 'QMS', to: '/dashboard', strong: true },
  { label: 'Capacitaciones', strong: true },
];

function Frame({ children }: { children: ReactNode }): ReactElement {
  // G4: sin permiso confirmado, las migas y el encabezado no nombran la pantalla.
  const perm = usePermissionState('quality:training:read');
  return (
    <>
      <GatedChrome state={perm.state} breadcrumb={BREADCRUMB} navContext="Capacitaciones" />
      <BackLink target={useBackTarget(TO_PANEL)} />
      {children}
    </>
  );
}

/** Pantalla Capacitaciones (so=360): avance de lectura de los documentos vigentes y
 *  competencias del personal, desde el LMS, para la empresa activa. */
export function Training(): ReactElement {
  const perm = usePermissionState('quality:training:read');
  const canRead = perm.state === 'granted';
  const { activeCompanyId, activeCompanyName, isLoading: companyLoading } = useActiveCompany();
  const { page, doc, setPage, setDoc, clearDoc } = useTrainingUrlState();
  const detailRef = useRef<HTMLDivElement>(null);
  const query = useTrainingOverview(page, {
    companyId: activeCompanyId,
    enabled: canRead && Boolean(activeCompanyId),
  });

  // Una página más allá del total (enlace viejo, documentos que bajaron) salta a la última.
  const total = query.data?.total ?? 0;
  const emptyPage = query.isSuccess && !query.isPlaceholderData && query.data.items.length === 0;
  useEffect(() => {
    if (emptyPage && total > 0) {
      setPage(Math.ceil(total / PAGE_SIZE), { replace: true });
    }
  }, [emptyPage, total, setPage]);

  // En móvil el detalle queda debajo de la tabla: elegir un documento lo trae a la vista.
  const select = (entryId: string): void => {
    setDoc(entryId);
    if (window.matchMedia('(max-width: 899px)').matches) {
      requestAnimationFrame(() => detailRef.current?.scrollIntoView({ block: 'start' }));
    }
  };

  if (perm.state === 'error') {
    return (
      <Frame>
        <PermissionErrorPanel error={perm.error} onRetry={perm.retry} retrying={perm.retrying} />
      </Frame>
    );
  }
  if (perm.state === 'denied') {
    return (
      <Frame>
        <RestrictedPanel />
      </Frame>
    );
  }
  if (canRead && !companyLoading && !activeCompanyId) {
    return (
      <Frame>
        <StatePanel icon={<Building2 />} title="Selecciona una empresa activa" role="status">
          Elige una empresa activa en la barra superior.
        </StatePanel>
      </Frame>
    );
  }

  const data = query.data;
  const items = data?.items ?? [];
  const selected = doc ?? items[0]?.entryId ?? null;
  const selectedRow = items.find((r) => r.entryId === selected);

  return (
    <Frame>
      <div className="pagehead rise">
        <div className="grow">
          <h1 style={{ marginBottom: 4 }}>Capacitaciones</h1>
          <p style={{ maxWidth: '70ch' }}>
            Avance de lectura de la versión vigente de cada documento y competencias del personal,
            según el LMS.
          </p>
        </div>
        {canRead && activeCompanyId && (
          <div className="pagehead-actions">
            {/* Exportación (spec 2026-10-14 §3): todos los documentos, no solo esta página. */}
            <ExportMenu path="/api/v1/quality/training/export" query="" title="Capacitaciones" />
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <TrainingKpis summary={data?.summary} companyName={activeCompanyName} />
      </div>

      {perm.state === 'loading' || companyLoading || query.isLoading ? (
        <TrainingSkeleton />
      ) : query.isError ? (
        <div style={{ marginTop: 16 }}>
          <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />
        </div>
      ) : (data?.total ?? 0) === 0 ? (
        <div style={{ marginTop: 16 }}>
          <StatePanel
            icon={<GraduationCap />}
            title="Sin capacitaciones registradas"
            role="status"
            className="d2"
          >
            Ningún documento vigente de la empresa activa tiene lecturas asignadas en el LMS.
          </StatePanel>
        </div>
      ) : (
        <div className="g-2" style={{ marginTop: 16 }}>
          <div className="card rise d2">
            <div className="ch">
              <h3>Capacitaciones por documento</h3>
              <span className="count">
                {(data?.total ?? 0).toLocaleString('es-CR')}{' '}
                {data?.total === 1 ? 'documento' : 'documentos'}
              </span>
            </div>
            <TrainingTable rows={items} selected={selected} onSelect={select} />
            {(data?.total ?? 0) > PAGE_SIZE && (
              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                total={data?.total ?? 0}
                busy={query.isFetching}
                onPage={setPage}
              />
            )}
          </div>
          <div ref={detailRef} style={{ minWidth: 0, scrollMarginTop: 16 }}>
            {selected !== null && (
              <DocumentDetail
                key={selected}
                entryId={selected}
                code={selectedRow?.code ?? null}
                assigned={selectedRow?.assigned ?? null}
                dispatch={selectedRow?.dispatch ?? null}
                onReset={clearDoc}
              />
            )}
          </div>
        </div>
      )}
    </Frame>
  );
}

function ErrorPanel({ error, onRetry }: { error: unknown; onRetry: () => void }): ReactElement {
  const copy = errorCopy(error);
  return (
    <StatePanel
      icon={<FileWarning />}
      title={copy.title}
      tone="danger"
      role="alert"
      onRetry={copy.retry ? onRetry : undefined}
      className="d2"
    >
      {copy.body}
    </StatePanel>
  );
}
